import React, { useEffect } from 'react';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { useDispatch, useSelector } from 'react-redux';
import { useLeadsCRUDActions } from '@mm/state/actions/LeadsCRUDActions';
import {
  ConvertationRequest,
  ConvertLeadsResponse,
  LeadsSearchQuery,
} from '@mm/state/interfaces/leads';
import { Button, notification } from 'antd';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { MODALS_TYPE } from '@mm/state/modules/modalsState/types';
import {
  activeFacebookPageSelector,
  selectAnnualPlansAllotments,
  selectPlansAllotments,
} from '@mm/state/selectors/facebookPageSelector';
import {
  LEAD_COST_IN_CREDITS,
  MAX_CONTACTS_ALLOWED,
} from './PurchaseContactsTable/PurchaseContactsTable';
import { enhancedPlansSelector } from '@mm/state/selectors/subscriptionSelectors';
import { InfoCircleOutlined } from '@ant-design/icons';
import { abbreviateNumber } from '@mm/utils';
import { getPlanAllotmentAndMaxLevelByPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import styles from './PurchaseContactsTable/PurchaseContactsTable.module.css';
import { Link } from '@mm/components';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import actions from '@mm/state/actions/actions';
import { selectFreezedCreditsSum } from '@mm/state/selectors/leadsSearchSelectors';
import { landmineNames } from '@mm/constants/landmine-names';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';

export const useConvertLeadsToContacts = ({
  filterParams,
}: {
  filterParams?: LeadsSearchQuery;
}) => {
  const {
    convertLeadsToContacts,
    searchLeads,
    retrieveConvertationRequests,
    keepBusy,
  } = useLeadsCRUDActions();
  const dispatch = useDispatch();
  const { retrievePage } = useConnectedPageCRUDActions();
  const {
    openConfirmPurchaseLeadsModal,
    openConvertLeadsSuccessModal,
    openAddAudienceToAutomationModal,
    openNewBillingModal2023,
    openBillingModal,
    openGeneralConfirmModal,
    closeModal,
  } = useModalActions();
  const history = useHistory();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const freezedCreditsSum = useSelector(selectFreezedCreditsSum);
  const currentSubscription = facebookPage?.subscription;

  const annualPlansAllotments = useSelector(selectAnnualPlansAllotments);
  const monthlyPlansAllotments = useSelector(selectPlansAllotments);
  const plans = useSelector(enhancedPlansSelector);
  const plansAllotments =
    currentSubscription?.billingPeriodLength === 'annually'
      ? annualPlansAllotments
      : monthlyPlansAllotments;

  const billingPlan = plans.find(
    plan =>
      currentSubscription && currentSubscription.planCode === plan.planCode,
  );

  const { planAllotment, maxSendsLevelForPlan } =
    getPlanAllotmentAndMaxLevelByPlanInfo({
      plansAllotments,
      plan: billingPlan,
      page: facebookPage,
    });

  const creditsAvailableCount = Math.floor(
    Number(facebookPage?.outgoingMessagesLimit) -
      Number(facebookPage?.outgoingMessageUsage?.subscriptionMessagingCount) -
      freezedCreditsSum,
  );

  useEffect(() => {
    retrievePage(facebookPageId, false, false, false, true);
    retrieveConvertationRequests(facebookPageId);
  }, []);

  const getPurchaseLeadsCount = (params: ConvertationRequest) =>
    params.leadIds?.length
      ? params.leadIds?.length
      : params?.searchBody?.limit
      ? params?.searchBody?.limit
      : MAX_CONTACTS_ALLOWED;

  const onLeadsRedirectClick = (audienceId?: number) => {
    [
      MODALS_TYPE.CONVERT_LEADS_TO_CONTACTS,
      MODALS_TYPE.CONVERTED_LEADS_SUCCCESS_MODAL,
      MODALS_TYPE.ADD_AUDIENCE_TO_AUTOMATION,
    ].forEach(modalType => closeModal(modalType));

    if (audienceId) {
      history.push(
        BotBuilderRoutes.audiences.getLink({ chatbotId, audienceId }),
      );
    } else {
      history.push(BotBuilderRoutes.audiences.path);
    }
    notification.destroy(audienceId);
  };

  const openNotification = (audienceId?: number) => {
    notification.success({
      message: 'Leads are purchased',
      description:
        'Go to My leads tab in order to add them to automations or export their information',
      btn: (
        <Button
          size="middle"
          type="primary"
          onClick={() => onLeadsRedirectClick(audienceId)}
        >
          Go to My Leads
        </Button>
      ),
    });
  };

  const searchLeadsByAttributes = () => {
    if (filterParams) {
      searchLeads(filterParams);
    }
  };

  const confirmPurchaseLeads = async (params: ConvertationRequest) => {
    const response = await convertLeadsToContacts(facebookPageId, params);

    if (response?.audience) {
      openNotification(response.audience?.id);
      localStorage.setItem('leadsAlert', response.audience?.id.toString());

      openConvertLeadsSuccessModal({
        convertedLeads: response,
        onNext: () => {
          searchLeadsByAttributes();
          openAddAudienceToAutomationModal({
            activeAudience: response.audience,
            onAfterClose: () => {
              onLeadsRedirectClick(response.audience?.id);
            },
            usePurchaseLeadsLayout: true,
            withAlert: true,
          });
        },
        onClose: () => onLeadsRedirectClick(response.audience?.id),
      });
    }

    return response as ConvertLeadsResponse;
  };

  const getLevelToUpgrade = (needMoreCreditsCount: number) => {
    if (facebookPage && planAllotment) {
      const needMoreLevels = Math.ceil(
        needMoreCreditsCount / planAllotment.msgPerLevel,
      );

      const currentSendsLevel = currentSubscription?.sendAllotmentLevel || 0;

      const upgradeToLevel = needMoreLevels + currentSendsLevel;

      return {
        id: upgradeToLevel,
        level: upgradeToLevel,
        name: (
          upgradeToLevel * planAllotment.msgPerLevel +
          planAllotment.start
        ).toString(),
      };
    }
  };

  const convertLeads = (params: ConvertationRequest) => {
    const purchaseLeadsCount = getPurchaseLeadsCount(params);
    const hasEnoughCredits =
      purchaseLeadsCount * LEAD_COST_IN_CREDITS <= creditsAvailableCount;
    const needMoreCreditsCount =
      purchaseLeadsCount * LEAD_COST_IN_CREDITS - creditsAvailableCount;
    const level = getLevelToUpgrade(needMoreCreditsCount);
    const isNewBillingSubscription =
      currentSubscription && isBilling2023Subscription(currentSubscription);
    const notEnoughLevelsInPlan =
      !isNewBillingSubscription && level && maxSendsLevelForPlan < level?.level;
    const isTeamCustomPlan =
      currentSubscription?.planSubtypes.includes('team_custom');
    const isFreeTrial = !!facebookPage?.redemption?.freeTrial;
    const lackOfCredits =
      (isFreeTrial || facebookPage?.free) &&
      !hasEnoughCredits &&
      !isNewBillingSubscription;

    if (
      (facebookPage?.redemption &&
        !currentSubscription?.active &&
        !isFreeTrial) ||
      (notEnoughLevelsInPlan && !isTeamCustomPlan) ||
      lackOfCredits
    ) {
      openNewBillingModal2023({
        landmineName: landmineNames['CONVERT-MERCURY-LEADS'],
        onSuccessBilling: () => {
          keepBusy();
          setTimeout(() => {
            notification.success({
              message: 'Subsription has been created',
              description: 'You can continue purchasing leads',
            });
            dispatch({ type: actions.UNBUSY });
          }, 5000); // BE asked for 5sec timeout because of caching issues
        },
        className: styles.billingModal,
        css: { header: styles.billingModalHeader },
        heading: currentSubscription
          ? 'Upgrade your account to purchase more leads.'
          : '',
        subheading: currentSubscription
          ? 'Your current plan does not have enough credit blocks.'
          : '',
      });
      return;
    }

    if (notEnoughLevelsInPlan && isTeamCustomPlan) {
      openGeneralConfirmModal({
        headerText: 'Not enough credits to complete purchase',
        messageText: (
          <div>
            <div>
              Your current plan does not support more credit blocks to complete
              this purchase.
            </div>
            <div>
              Contact your sales representative at{' '}
              <Link
                isBlank
                style={{ fontSize: '16px' }}
                href="mailto:sales@customers.ai"
                capitalize={false}
              >
                sales@customers.ai
              </Link>{' '}
              to increase the quote.
            </div>
          </div>
        ),
        showCancelButton: false,
        onCloseHandler: () => closeModal(),
        okButtonStyling: { blue: true },
      });
      return;
    }

    if (
      !hasEnoughCredits &&
      facebookPage &&
      billingPlan &&
      !isNewBillingSubscription
    ) {
      openBillingModal({
        facebookPage,
        plan: billingPlan,
        level,
        manualRedirect: true,
        subscription: currentSubscription,
        onSuccessBilling: () => {
          keepBusy();
          setTimeout(async () => {
            await confirmPurchaseLeads(params);
            dispatch({ type: actions.UNBUSY });
          }, 5000); // BE asked for 5sec timeout because of caching issues
        },
        extraSendsOnly: true,
        alert: (
          <div className={styles.alert}>
            <InfoCircleOutlined className={styles.alertIcon} />
            <span className={styles.alertTitle}>
              You need {abbreviateNumber(needMoreCreditsCount)} more Credits to
              purchase {purchaseLeadsCount.toLocaleString('en-US')} leads
            </span>
          </div>
        ),
      });
      return;
    }

    if (!hasEnoughCredits && !billingPlan) {
      console.error('Matching plan not found');
      return;
    }

    openConfirmPurchaseLeadsModal({
      onConfirm: () => confirmPurchaseLeads(params),
      params,
    });
  };
  return {
    convertLeads,
    creditsAvailableCount,
  };
};
